import { useCallback, useEffect, useState } from "react";
import { Scale, AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function RatiosPage() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/daycare/ratios"); setData(d); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load ratios"); }
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const setGroup = async (classId, age_group) => {
    await api.put(`/daycare/rooms/${classId}/age-group`, { age_group });
    load();
  };

  if (!data) return <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="ratios-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><Scale className="h-3.5 w-3.5" /> Compliance</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Staff-to-child ratios</h1>
          <p className="text-sm text-slate-500">Live count of children signed in vs. teachers clocked in for each room. Required ratios come from the age group (edit in Settings).</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 h-9 text-sm text-slate-700 hover:bg-slate-50" data-testid="ratios-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </header>

      {data.alerts > 0 ? (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" data-testid="ratio-alert-banner">
          <AlertTriangle className="h-4 w-4" /> {data.alerts} room{data.alerts === 1 ? " is" : "s are"} over ratio right now.
        </div>
      ) : (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" data-testid="ratio-ok-banner">
          <CheckCircle2 className="h-4 w-4" /> All rooms are within ratio.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.rooms.map((r) => (
          <div key={r.class_id} className={`rounded-2xl border-2 bg-white p-5 ${r.over_ratio ? "border-red-300" : "border-slate-200"}`} data-testid={`ratio-room-${r.class_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500">{r.room}{r.teacher_name ? ` · ${r.teacher_name}` : ""}</div>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.over_ratio ? "bg-red-100 text-red-700" : r.children_present ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`ratio-status-${r.class_id}`}>
                {r.over_ratio ? "Over ratio" : r.children_present ? "OK" : "Empty"}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-slate-50 py-3"><div className="text-2xl font-bold text-slate-900">{r.children_present}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Children</div></div>
              <div className="rounded-xl bg-slate-50 py-3"><div className="text-2xl font-bold text-slate-900">{r.staff_present}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Staff in</div></div>
              <div className="rounded-xl bg-slate-50 py-3"><div className={`text-2xl font-bold ${r.over_ratio ? "text-red-600" : "text-slate-900"}`}>{r.ratio_text}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Now</div></div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-600">
              <span>Required {r.required_ratio ? `1:${r.required_ratio}` : "—"}{r.staff_needed ? ` · need ${r.staff_needed} staff` : ""}</span>
              <select value={r.age_group || ""} onChange={(e) => setGroup(r.class_id, e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid={`age-group-${r.class_id}`}>
                <option value="">Age group…</option>
                {data.age_groups.map((g) => <option key={g.key} value={g.key}>{g.name} (1:{g.ratio})</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
